import type {
    SessionHandler,
    UserEventHandler,
    User as SendbirdUser,
    SendbirdError,
    BannedUserListQueryParams,
} from '@sendbird/chat';
import SendbirdChat, { Role } from '@sendbird/chat';
import type {
    GroupChannel,
    GroupChannelCreateParams,
    GroupChannelHandler,
    GroupChannelListQuery,
    GroupChannelListQueryParams,
    SendbirdGroupChat,
    GroupChannelHideParams,
    Member,
    ReadStatus,
    MemberListQueryParams,
} from '@sendbird/chat/groupChannel';
import {
    GroupChannelSearchField,
    GroupChannelModule,
    MyMemberStateFilter,
    GroupChannelListOrder,
    MemberListOrder,
    OperatorFilter,
} from '@sendbird/chat/groupChannel';
import type { BaseMessage, FileMessage, MessageListParams } from '@sendbird/chat/message';
import { MessageModule, MessageTypeFilter, ReplyType } from '@sendbird/chat/message';
import * as Sentry from '@sentry/react';

import type {
    ChatNotificationType,
    DataType,
    InputMaybe,
    MessageDataPayload,
    User,
} from 'src/types/__generated__/graphql';

export interface UserMetadata {
    activityLabelKey?: string;
    initials?: string;
}

export interface SendbirdMember extends Omit<Member, 'metaData'> {
    metaData: UserMetadata;
}

interface SendbirdReader extends Omit<SendbirdUser, 'metaData'> {
    metaData: UserMetadata;
}

export interface SendbirdReadStatus extends Omit<ReadStatus, 'reader'> {
    reader: SendbirdReader;
}

export const GROUP_CONVERSATION_CUSTOM_TYPE = 'GROUP_CONVERSATION';
export const messagesQueryLimit = 20;
export const channelsQueryLimit = 10;
const maxInvitedMembersCount = 99;

let sendbird: SendbirdGroupChat | undefined;

const init = (sessionHandler: SessionHandler) => {
    try {
        sendbird = SendbirdChat.init({
            appId: process.env.NEXT_PUBLIC_SENDBIRD_APP_ID as string,
            modules: [new GroupChannelModule(), new MessageModule()],
        }) as SendbirdGroupChat;

        sendbird.setSessionHandler(sessionHandler);
    } catch (error: unknown) {
        logSendbirdError('init', error as SendbirdError);
        throw error;
    }
};

export const initIfNeeded = (sessionHandler: SessionHandler) => {
    if (!sendbird) {
        init(sessionHandler);
    }
};

export const connect = async (userId: string, userToken: string) => {
    try {
        await sendbird?.connect(userId, userToken);
    } catch (error: unknown) {
        logSendbirdError('connect', error as SendbirdError);
        throw error;
    }
};

export const disconnect = async () => {
    try {
        await sendbird?.disconnect();
    } catch (error: unknown) {
        logSendbirdError('disconnect', error as SendbirdError);
        if (process.env.NODE_ENV !== 'test') {
            throw error;
        }
    }
};

interface createGroupChannelsQueryParams {
    search?: string;
    filterType?: 'groupConversations' | '1o1Conversations';
}

export const createGroupChannelsQuery = (params?: createGroupChannelsQueryParams) => {
    const { search, filterType } = params ?? { search: undefined, filterType: undefined };
    let customTypesFilter;
    if (filterType) {
        if (filterType === 'groupConversations') {
            customTypesFilter = [GROUP_CONVERSATION_CUSTOM_TYPE];
        }
        if (filterType === '1o1Conversations') {
            customTypesFilter = [''];
        }
    }

    const queryParams: GroupChannelListQueryParams = {
        includeEmpty: false,
        searchFilter: search
            ? {
                  fields: [GroupChannelSearchField.CHANNEL_NAME, GroupChannelSearchField.MEMBER_NICKNAME],
                  query: search,
              }
            : undefined,
        myMemberStateFilter: MyMemberStateFilter.JOINED,
        order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
        limit: channelsQueryLimit,
        customTypesFilter,
    };
    try {
        return sendbird?.groupChannel.createMyGroupChannelListQuery(queryParams);
    } catch (error: unknown) {
        logSendbirdError('createGroupChannelsQuery', error as SendbirdError);
        throw error;
    }
};

export const createGroupChannelsQueryByUrl = (talkWithChannelUrl: string) => {
    const params: GroupChannelListQueryParams = {
        includeEmpty: false,
        channelUrlsFilter: [talkWithChannelUrl],
        myMemberStateFilter: MyMemberStateFilter.JOINED,
        order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
        limit: channelsQueryLimit,
    };
    try {
        return sendbird?.groupChannel.createMyGroupChannelListQuery(params);
    } catch (error: unknown) {
        logSendbirdError('createGroupChannelsQueryByUrl', error as SendbirdError);
        throw error;
    }
};

export const getUserChannels = async (groupChannelsQuery: GroupChannelListQuery) => {
    try {
        if (groupChannelsQuery.hasNext) {
            const channels = await groupChannelsQuery.next();
            return channels;
        }

        return [];
    } catch (error: unknown) {
        logSendbirdError('getUserChannels', error as SendbirdError);
        throw error;
    }
};

export const getPreviousMessagesByTimestamp = async (channel: GroupChannel, timestamp: number) => {
    const params: MessageListParams = {
        prevResultSize: messagesQueryLimit,
        nextResultSize: 0,
        isInclusive: false,
        reverse: true,
        messageTypeFilter: MessageTypeFilter.ALL,
        replyType: ReplyType.ALL,
    };

    try {
        const messages = await channel.getMessagesByTimestamp(timestamp, params);
        return messages;
    } catch (error: unknown) {
        logSendbirdError('getPreviousMessagesByTimestamp', error as SendbirdError);
        throw error;
    }
};

export const addGroupChannelHandler = (groupChannelHandler: GroupChannelHandler) => {
    try {
        sendbird?.groupChannel.addGroupChannelHandler('GROUP_CHANNEL_HANDLER', groupChannelHandler);
    } catch (error: unknown) {
        logSendbirdError('addGroupChannelHandler', error as SendbirdError);
        throw error;
    }
};

export const removeGroupChannelHandler = () => {
    try {
        sendbird?.groupChannel.removeGroupChannelHandler('GROUP_CHANNEL_HANDLER');
    } catch (error: unknown) {
        logSendbirdError('removeGroupChannelHandler', error as SendbirdError);
        throw error;
    }
};

export const addUserEventHandler = (userEventHandler: UserEventHandler) => {
    try {
        sendbird?.addUserEventHandler('USER_EVENT_HANDLER', userEventHandler);
    } catch (error: unknown) {
        logSendbirdError('addUserEventHandler', error as SendbirdError);
        throw error;
    }
};

export const removeUserEventHandler = () => {
    try {
        sendbird?.removeUserEventHandler('USER_EVENT_HANDLER');
    } catch (error: unknown) {
        logSendbirdError('removeUserEventHandler', error as SendbirdError);
        throw error;
    }
};

export const getTotalUnreadMessageCount = async () => {
    try {
        const totalUnreadMessages = await sendbird?.groupChannel.getTotalUnreadMessageCount();
        return totalUnreadMessages;
    } catch (error: unknown) {
        logSendbirdError('getTotalUnreadMessageCount', error as SendbirdError);
        throw error;
    }
};

export const createUsersChannel = async (
    currentSendbirdUserId: string,
    contactedSendbirdUserIds: string[],
    channelName = '',
    isGroupConversation?: boolean,
) => {
    // The max number of invitedUserIds is 100 so we create a list with the 99 first and an other list with the others
    const first99 = contactedSendbirdUserIds.slice(0, maxInvitedMembersCount);
    const others = contactedSendbirdUserIds.slice(maxInvitedMembersCount);

    const params: GroupChannelCreateParams = {
        invitedUserIds: [currentSendbirdUserId, ...first99],
        operatorUserIds: [currentSendbirdUserId],
        isDistinct: !isGroupConversation, // Use isDistinct if conversation is 1 to 1
        isSuper: contactedSendbirdUserIds.length > maxInvitedMembersCount - 1, // Need supergroup if more than 99 members (-1 because contactedSendbirdUserIds doesn't include creator)
        name: channelName,
        customType: isGroupConversation ? GROUP_CONVERSATION_CUSTOM_TYPE : undefined,
    };
    try {
        const channel = (await sendbird?.groupChannel.createChannel(params)) as GroupChannel;

        // If others length > 0, it means we couldn't add all users while creation, so we add them now
        if (others.length > 0) {
            await inviteUsersToChannel(channel, others);
            // We refresh the channel data because the number of members is not updated automatically
            await channel.refresh();
        }

        return channel;
    } catch (error: unknown) {
        logSendbirdError('createUsersChannel', error as SendbirdError);
        throw error;
    }
};

export const createGroupConversation = async (
    currentUser: Pick<User, 'sendbirdUserId'>,
    selectedUsers: User[],
    groupConversationName?: string,
) => {
    if (currentUser.sendbirdUserId) {
        const sendbirdIds = selectedUsers.map((u) => (u as User).sendbirdUserId) as string[];
        const channel = await createUsersChannel(
            currentUser.sendbirdUserId,
            sendbirdIds,
            groupConversationName,
            Boolean(groupConversationName),
        );
        return channel;
    }
    return null;
};

export const archiveChannel = async (channelUrl: string) => {
    try {
        const params: GroupChannelHideParams = {
            hidePreviousMessages: false,
            allowAutoUnhide: true,
        };
        const channel = await sendbird?.groupChannel.getChannel(channelUrl);
        await channel?.hide(params);
    } catch (error: unknown) {
        logSendbirdError('archiveChannel', error as SendbirdError);
        throw error;
    }
};

export const markChannelAsRead = async (channel: GroupChannel) => {
    try {
        await channel.markAsRead();
    } catch (error: unknown) {
        logSendbirdError('markChannelAsRead', error as SendbirdError);
        throw error;
    }
};

export const createChannelMemberListQuery = (channel: GroupChannel, search?: string, limit = 100) => {
    try {
        const params: MemberListQueryParams = {
            limit,
            order: MemberListOrder.OPERATOR_THEN_MEMBER_ALPHABETICAL,
            nicknameStartsWithFilter: search,
        };
        return channel.createMemberListQuery(params);
    } catch (error: unknown) {
        logSendbirdError('createChannelMemberListQuery', error as SendbirdError);
        throw error;
    }
};

export const getChannelOperatorsCount = async (channel: GroupChannel) => {
    try {
        const params: MemberListQueryParams = {
            limit: 100,
            operatorFilter: OperatorFilter.OPERATOR, // Doesn't work but keep it (who knows)
            order: MemberListOrder.OPERATOR_THEN_MEMBER_ALPHABETICAL,
        };

        const query = channel.createMemberListQuery(params);

        let operatorsCount = 0;
        do {
            // eslint-disable-next-line no-await-in-loop -- need it to fet all operators
            const members = await query.next();

            const operators = members.filter((m) => m.role === Role.OPERATOR);
            operatorsCount += operators.length;

            // as the list is orber by "operators then member", if there is members length > operators length,
            // it means there are no more operators to fetch, so we brek the loop
            if (operators.length < members.length) {
                break;
            }
        } while (query.hasNext);

        return operatorsCount;
    } catch (error: unknown) {
        logSendbirdError('getChannelOperatorsCount', error as SendbirdError);
        throw error;
    }
};

export const createChannelBannedUserListQuery = (channel: GroupChannel) => {
    try {
        const params: BannedUserListQueryParams = {
            limit: 100,
        };
        return channel.createBannedUserListQuery(params);
    } catch (error: unknown) {
        logSendbirdError('createChannelBannedUserListQuery', error as SendbirdError);
        throw error;
    }
};

export const banMemberFromChannel = async (channel: GroupChannel, userId: string) => {
    try {
        await channel.banUserWithUserId(userId);
    } catch (error: unknown) {
        logSendbirdError('banMemberFromChannel', error as SendbirdError);
        throw error;
    }
};

export const unbanMemberFromChannel = async (channel: GroupChannel, userIds: string[]) => {
    try {
        const unbanPromises = userIds.map((userId) => channel.unbanUserWithUserId(userId));
        await Promise.all(unbanPromises);
    } catch (error: unknown) {
        logSendbirdError('unbanMemberFromChannel', error as SendbirdError);
        throw error;
    }
};

export const inviteUsersToChannel = async (channel: GroupChannel, userIds: string[]) => {
    // The max number of invitedUserIds is 100 so we create chunks of 99
    const invitedUserIdsListChunks: string[][] = [];
    const chunkSize = maxInvitedMembersCount;
    for (let i = 0; i < userIds.length; i += chunkSize) {
        invitedUserIdsListChunks.push(userIds.slice(i, i + chunkSize));
    }

    try {
        for (const invitedUserChunk of invitedUserIdsListChunks) {
            // eslint-disable-next-line no-await-in-loop -- need it
            await channel.inviteWithUserIds(invitedUserChunk);
        }
    } catch (error: unknown) {
        logSendbirdError('inviteUsersToChannel', error as SendbirdError);
        throw error;
    }
};

export const addModeratorsToChannel = async (channel: GroupChannel, userIds: string[]) => {
    try {
        await channel.addOperators(userIds);
    } catch (error: unknown) {
        logSendbirdError('addModeratorsToChannel', error as SendbirdError);
        throw error;
    }
};

export const removeModeratorsFromChannel = async (channel: GroupChannel, userIds: string[]) => {
    try {
        await channel.removeOperators(userIds);
    } catch (error: unknown) {
        logSendbirdError('removeModeratorsFromChannel', error as SendbirdError);
        throw error;
    }
};

interface LeaveChannelChatNotificationData {
    notificationType: ChatNotificationType.LeaveChannel;
}

interface ChannelCreationChatNotificationData {
    notificationType: ChatNotificationType.ChannelCreation;
}

export interface RemoveUserNotificationData {
    removedUserName: string;
    removedSendbirdUserId: string;
}

interface RemoveUserChatNotificationData {
    notificationType: ChatNotificationType.RemoveUser;
    data: RemoveUserNotificationData;
}

export interface AddUsersNotificationData {
    firstAddedUserName: string;
    numberOfAddedUsers: number;
}

export interface UpdateGroupNameNotificationData {
    userName: string;
    groupName: string;
}

export interface UpdateGroupPictureNotificationData {
    userName: string;
}

interface AddUsersChatNotificationData {
    notificationType: ChatNotificationType.AddUsers;
    data: AddUsersNotificationData;
}

interface UpdateGroupNameChatNotificationData {
    notificationType: ChatNotificationType.UpdateGroupName;
    data: UpdateGroupNameNotificationData;
}

interface UpdateGroupPictureChatNotificationData {
    notificationType: ChatNotificationType.UpdateGroupPicture;
    data: UpdateGroupPictureNotificationData;
}

type ChatNotificationDataCustom =
    | LeaveChannelChatNotificationData
    | ChannelCreationChatNotificationData
    | RemoveUserChatNotificationData
    | AddUsersChatNotificationData
    | UpdateGroupNameChatNotificationData
    | UpdateGroupPictureChatNotificationData;

type CustomPayload = InputMaybe<MessageDataPayload> & {
    files?: Array<{
        id: string;
    }>;
};
export type MessageDataWithFiles = Partial<ChatNotificationDataCustom> & {
    payload?: CustomPayload;
    type: DataType;
};

interface ReadUser {
    userId: string;
    nickName: string;
    profileUrl?: string;
    initials?: string;
}

type IMessageSender = {
    userId: string;
    nickname: string;
    profileUrl?: string;
};

export interface IMessage {
    id?: number;
    message?: string;
    sender: IMessageSender;
    isMyMessage: boolean;
    createdAt: number;
    reqId: string;
    data?: MessageDataWithFiles;
    baseMessage?: BaseMessage;
    channel: GroupChannel;
    lastMessageReadBy: ReadUser[];
}

export const baseMessagetoIMessage = (message: BaseMessage, currentUser: User, channel: GroupChannel): IMessage => {
    if (message.isUserMessage()) {
        return {
            id: message.messageId,
            message: message.message,
            sender: message.sender,
            isMyMessage: message.sender.userId === currentUser.sendbirdUserId,
            createdAt: message.createdAt,
            reqId: message.reqId,
            data: message.data ? (JSON.parse(message.data) as MessageDataWithFiles) : undefined,
            baseMessage: message,
            channel,
            lastMessageReadBy: [],
        };
    } else {
        return {
            id: message.messageId,
            message: undefined,
            sender: (message as FileMessage).sender,
            isMyMessage: (message as FileMessage).sender.userId === currentUser.sendbirdUserId,
            createdAt: message.createdAt,
            reqId: (message as FileMessage).reqId,
            data: undefined,
            baseMessage: message,
            channel,
            lastMessageReadBy: [],
        };
    }
};

const logSendbirdError = (origin: string, error: SendbirdError) => {
    if (process.env.NEXT_PUBLIC_ENV && process.env.NEXT_PUBLIC_ENV !== 'local') {
        Sentry.captureException(new Error(`Sendbird Error : ${origin} - ${error.code}`));
    }
};
